import type { Editor } from "@tiptap/react";
import {
  Bold,
  Italic,
  Underline,
  Heading1,
  Heading2,
  Heading3,
  List,
  ListOrdered,
  AlignLeft,
  AlignCenter,
  AlignRight,
  Table,
  Undo2,
  Redo2,
} from "lucide-react";
import { cn } from "@/lib/utils";

interface ProtocolEditorToolbarProps {
  editor: Editor | null;
}

export function ProtocolEditorToolbar({ editor }: ProtocolEditorToolbarProps) {
  if (!editor) return null;

  const groups: ToolbarGroup[] = [
    {
      items: [
        {
          icon: Undo2,
          action: () => editor.chain().focus().undo().run(),
          disabled: !editor.can().undo(),
          title: "Undo",
        },
        {
          icon: Redo2,
          action: () => editor.chain().focus().redo().run(),
          disabled: !editor.can().redo(),
          title: "Redo",
        },
      ],
    },
    {
      items: [
        {
          icon: Bold,
          action: () => editor.chain().focus().toggleBold().run(),
          active: editor.isActive("bold"),
          title: "Bold",
        },
        {
          icon: Italic,
          action: () => editor.chain().focus().toggleItalic().run(),
          active: editor.isActive("italic"),
          title: "Italic",
        },
        {
          icon: Underline,
          action: () => editor.chain().focus().toggleUnderline().run(),
          active: editor.isActive("underline"),
          title: "Underline",
        },
      ],
    },
    {
      items: [
        {
          icon: Heading1,
          action: () =>
            editor.chain().focus().toggleHeading({ level: 1 }).run(),
          active: editor.isActive("heading", { level: 1 }),
          title: "Heading 1",
        },
        {
          icon: Heading2,
          action: () =>
            editor.chain().focus().toggleHeading({ level: 2 }).run(),
          active: editor.isActive("heading", { level: 2 }),
          title: "Heading 2",
        },
        {
          icon: Heading3,
          action: () =>
            editor.chain().focus().toggleHeading({ level: 3 }).run(),
          active: editor.isActive("heading", { level: 3 }),
          title: "Heading 3",
        },
      ],
    },
    {
      items: [
        {
          icon: List,
          action: () => editor.chain().focus().toggleBulletList().run(),
          active: editor.isActive("bulletList"),
          title: "Bullet List",
        },
        {
          icon: ListOrdered,
          action: () => editor.chain().focus().toggleOrderedList().run(),
          active: editor.isActive("orderedList"),
          title: "Ordered List",
        },
      ],
    },
    {
      items: [
        {
          icon: AlignLeft,
          action: () => editor.chain().focus().setTextAlign("left").run(),
          active: editor.isActive({ textAlign: "left" }),
          title: "Align Left",
        },
        {
          icon: AlignCenter,
          action: () => editor.chain().focus().setTextAlign("center").run(),
          active: editor.isActive({ textAlign: "center" }),
          title: "Align Center",
        },
        {
          icon: AlignRight,
          action: () => editor.chain().focus().setTextAlign("right").run(),
          active: editor.isActive({ textAlign: "right" }),
          title: "Align Right",
        },
      ],
    },
    {
      items: [
        {
          icon: Table,
          action: () =>
            editor
              .chain()
              .focus()
              .insertTable({ rows: 3, cols: 3, withHeaderRow: true })
              .run(),
          title: "Insert Table",
        },
      ],
    },
  ];

  return (
    <div className="flex flex-wrap items-center gap-0.5 border-b border-border bg-muted/50 px-2 py-1.5">
      {groups.map((group, gi) => (
        <div key={gi} className="flex items-center gap-0.5">
          {gi > 0 && (
            <div className="mx-1 h-5 w-px bg-border" />
          )}
          {group.items.map((item, ii) => (
            <button
              key={ii}
              onClick={item.action}
              disabled={item.disabled}
              title={item.title}
              className={cn(
                "flex h-7 w-7 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-muted hover:text-foreground disabled:opacity-30",
                item.active && "bg-primary/10 text-primary",
              )}
            >
              <item.icon className="h-3.5 w-3.5" />
            </button>
          ))}
        </div>
      ))}
    </div>
  );
}

interface ToolbarItem {
  icon: React.ComponentType<{ className?: string }>;
  action: () => void;
  active?: boolean;
  disabled?: boolean;
  title: string;
}

interface ToolbarGroup {
  items: ToolbarItem[];
}
